import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <SEO 
        title="About Us - Premium Corporate Wear & Gifting"
        description="Branding Yard is a premium corporate wear and corporate gifting company dedicated to helping businesses express their identity with elegance, consistency, and distinction."
        url="/about"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'About Us' }
        ]}
      />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-[#0A0A0A]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">
              About Us
            </p>
            <h1 className="font-serif text-4xl md:text-6xl font-semibold tracking-tight text-white leading-tight">
              Branding Yard
            </h1>
            <p className="mt-6 text-[#A3A3A3] leading-relaxed text-lg max-w-2xl">
              At Branding Yard, we believe presentation is power. We are a premium corporate wear and corporate gifting company dedicated to helping businesses express their identity with elegance, consistency, and distinction.
            </p>
          </div>
        </div>
      </section>

       {/* Mission & Vision Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-16">
            <div className="bg-white p-12 border border-[#E5E5E5]">
              <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">Our Mission</p>
              <h3 className="font-serif text-2xl md:text-3xl font-medium tracking-tight text-[#0A0A0A] mb-6">
                Delivering Premium Excellence
              </h3>
              <p className="text-[#737373] leading-relaxed text-lg">
                To deliver premium corporate wear and bespoke corporate gifting solutions that reflect excellence, elevate brand identity, and leave a lasting impression. Through superior quality, thoughtful design, and meticulous attention to detail, we help businesses present their brands with distinction and professionalism.
              </p>
            </div>

            <div className="bg-white p-12 border border-[#E5E5E5]">
              <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">Our Vision</p>
              <h3 className="font-serif text-2xl md:text-3xl font-medium tracking-tight text-[#0A0A0A] mb-6">
                Crafting Distinctive Brands
              </h3>
              <p className="text-[#737373] leading-relaxed text-lg">
                To craft distinctive brands of exceptional quality that command attention, inspire confidence, and endure with elegance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section 
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Content - takes 3 columns 
            <div className="lg:col-span-3">
              <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">Who We Are</p>
              <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-[#0A0A0A]">
                Your Brand Deserves to Be Experienced at Its Best
              </h2>
              <p className="mt-6 text-[#737373] leading-relaxed text-lg">
                Every garment we produce and every gift we curate is thoughtfully designed to reflect the professionalism, values, and prestige of the brands we serve. From executive apparel to bespoke branded gifts, our work is guided by one principle: your brand deserves to be experienced at its best.
              </p>
              
              {/* Key Highlights 
              <div className="mt-10 grid sm:grid-cols-3 gap-6">
                <div className="border-l-2 border-[#C9A962] pl-4">
                  <p className="font-serif text-3xl font-semibold text-[#0A0A0A]">100%</p>
                  <p className="text-sm text-[#737373] mt-1">Quality Commitment</p>
                </div>
                <div className="border-l-2 border-[#C9A962] pl-4">
                  <p className="font-serif text-3xl font-semibold text-[#0A0A0A]">500+</p>
                  <p className="text-sm text-[#737373] mt-1">Brands Served</p>
                </div>
                <div className="border-l-2 border-[#C9A962] pl-4">
                  <p className="font-serif text-3xl font-semibold text-[#0A0A0A]">10+</p>
                  <p className="text-sm text-[#737373] mt-1">Years Experience</p>
                </div>
              </div>
            </div>
            
            {/* Image - takes 2 columns 
            <div className="lg:col-span-2 relative">
              <div className="aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1654785419449-7f0a9383a4fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwb2ZmaWNlJTIwZGVzayUyMGRpYXJ5JTIwbGFwdG9wfGVufDB8fHx8MTc2ODgyNDk5OHww&ixlib=rb-4.1.0&q=85"
                  alt="Premium corporate branding"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative accent 
              <div className="hidden lg:block absolute -bottom-6 -left-6 w-24 h-24 border-2 border-[#C9A962] -z-10"></div>
            </div>
          </div>
        </div>
      </section>
*/}
      {/* What We Do Section */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">What We Do</p>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-[#0A0A0A] max-w-3xl mx-auto">
              High-Quality Corporate Solutions That Elevate Brand Perception
            </h2>
            <p className="mt-6 text-[#737373] leading-relaxed text-lg max-w-2xl mx-auto">
              We specialize in high-quality corporate solutions that elevate brand perception and create lasting impressions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-3">Premium Corporate Wear & Uniforms</h3>
              <p className="text-[#737373]">
                High-quality garments designed to reflect professionalism and brand identity.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-3">Branded Executive & Staff Apparel</h3>
              <p className="text-[#737373]">
                Distinguished apparel that commands respect and reinforces brand consistency.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-3">Bespoke Corporate Gifts</h3>
              <p className="text-[#737373]">
                Thoughtfully curated gifts that leave lasting impressions on clients and partners.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-3">Promotional Merchandise</h3>
              <p className="text-[#737373]">
                Refined promotional items with superior finishes that represent your brand beautifully.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#E5E5E5]">
              <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-3">Brand Presentation Solutions</h3>
              <p className="text-[#737373]">
                Visual consistency solutions that ensure your brand is always presented at its best.
              </p>
            </div>
          </div>

          <p className="mt-12 text-center text-[#737373] leading-relaxed text-lg">
            Our offerings are carefully selected and crafted to ensure superior quality, durability, and aesthetic appeal.
          </p>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">Our Approach</p>
              <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-[#0A0A0A]">
                Creativity Combined With Precision
              </h2>
              <p className="mt-6 text-[#737373] leading-relaxed text-lg">
                At Branding Yard, we combine creativity with precision. We take time to understand each client's brand, audience, and objectives, allowing us to deliver solutions that are not only visually compelling but strategically aligned.
              </p>
            </div>

            <div className="space-y-12">
              <div className="border-l-2 border-[#0A0A0A] pl-8">
                <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-2">
                  Exceptional Attention to Detail
                </h3>
                <p className="text-[#737373]">
                  Every element is meticulously crafted to ensure perfection in every product we deliver.
                </p>
              </div>

              <div className="border-l-2 border-[#E5E5E5] pl-8">
                <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-2">
                  High-Quality Materials & Finishes
                </h3>
                <p className="text-[#737373]">
                  We source only the finest materials to create products that stand the test of time.
                </p>
              </div>

              <div className="border-l-2 border-[#E5E5E5] pl-8">
                <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-2">
                  Tailored Solutions for Each Client
                </h3>
                <p className="text-[#737373]">
                  We understand that every brand is unique, and we customize our solutions accordingly.
                </p>
              </div>

              <div className="border-l-2 border-[#E5E5E5] pl-8">
                <h3 className="font-serif text-xl font-semibold text-[#0A0A0A] mb-2">
                  Reliable Service & Professional Delivery
                </h3>
                <p className="text-[#737373]">
                  Count on us for consistent, dependable service from consultation to delivery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Parallax Quote Section */}
      <section 
        className="relative min-h-[50vh] flex items-center justify-center bg-fixed bg-center bg-cover"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=2000&q=80')`
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#0A0A0A]/75"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-screen-xl mx-auto px-6 md:px-12 lg:px-24 text-center py-24">
          <div className="max-w-3xl mx-auto">
            <p className="text-[#C9A962] text-sm uppercase tracking-widest mb-6">
              Our Promise
            </p>
            <blockquote className="font-serif text-2xl md:text-4xl font-medium text-white leading-relaxed">
              "Excellence is not a destination but a continuous journey. Every stitch, every detail, every product we create carries the weight of your brand's reputation."
            </blockquote>
            <div className="mt-8 flex items-center justify-center gap-4">
              <div className="w-12 h-[1px] bg-[#C9A962]"></div>
              <p className="text-[#A3A3A3] text-sm uppercase tracking-wide">Branding Yard</p>
              <div className="w-12 h-[1px] bg-[#C9A962]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#737373] mb-4">Why Choose Branding Yard</p>
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-[#0A0A0A] max-w-3xl mx-auto">
              Because Your Brand Deserves More Than Ordinary
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <p className="text-[#737373] leading-relaxed text-lg text-center">
              We partner with businesses that value excellence and understand the power of refined brand presentation. With Branding Yard, you are not just investing in products — you are investing in perception, credibility, and impact.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight text-white max-w-3xl mx-auto">
            Branding Yard – Where Professional Image Meets Refined Branding
          </h2>
          <p className="mt-6 text-[#A3A3A3] max-w-xl mx-auto">
            Discover our premium collection and elevate your brand presence.
          </p>
          <Button 
            asChild
            className="mt-10 bg-white text-[#0A0A0A] hover:bg-[#F5F5F5] rounded-none h-14 px-12 btn-lift text-sm uppercase tracking-wide"
          >
            <Link to="/store">
              Shop Now
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
